import React from "react";

import { Modal } from "shared/components/Modal/Modal";

import StatusIcon, { StatusType, getStatusColor } from "../../StatusIcon";

import { CardDetailItem } from "./ApplicantGroupCard";

// Define types for the modal details
export interface ModalRecordItem {
  text: string;
  id?: string;
}

export interface ModalSubItem {
  label: string;
  value: string;
  status?: string;
  records?: ModalRecordItem[];
  id?: string;
}

export interface ModalDetailItem {
  label: string;
  value: string;
  status?: string;
  subItems?: ModalSubItem[];
  id?: string;
}

export interface ModalSection {
  title?: string;
  items: ModalDetailItem[];
  id?: string;
}

export interface ApplicantDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardTitle: string;
  cardDetails: CardDetailItem[];
  cardModalDetails?: ModalSection[];
  status: StatusType;
}

// Styles for consistent item appearance
const itemStyle = {
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "24px",
  background: "#fafafa",
};

/**
 * Modal component for displaying detailed applicant information
 */
const ApplicantDetailModal: React.FC<ApplicantDetailModalProps> = ({
  isOpen,
  onClose,
  cardTitle,
  cardDetails,
  cardModalDetails,
  status,
}) => {
  // Use modal details if available, otherwise fall back to card details
  const hasModalDetails = cardModalDetails && cardModalDetails.length > 0;

  // Helper to create unique keys from item data
  const createSubItemKey = (subItem: ModalSubItem) =>
    subItem.id || `subitem-${subItem.label}-${subItem.value}`;

  return (
    <Modal
      title={<strong className="app-summary-v2 vega title">{cardTitle}</strong>}
      visible={isOpen}
      close={onClose}
      afterClose={onClose}
      showSubmitButton={false}
      showCancelLink={false}
      titleIcon={
        <StatusIcon status={status} style={{ height: 24, width: 24 }} />
      }
      destroyOnClose
      keyboard
      maskClosable
      style={{ top: 20 }}
    >
      <Modal.Body>
        <div
          className="app-summary-v2 vega flex column items-center"
          style={{ width: "100%", textAlign: "center" }}
        >
          {hasModalDetails ? (
            // Render detailed modal content
            <>
              {cardModalDetails.map((section) => (
                <div
                  key={section.id || `section-${section.title}`}
                  className="vega margin-bottom-medium"
                  style={{ width: "100%", maxWidth: "500px" }}
                >
                  {section.title && (
                    <h3 className="vega heading margin-bottom-small text-center">
                      {section.title}
                    </h3>
                  )}

                  {section.items.map((item) => (
                    <div
                      key={item.id || `item-${item.label}-${item.value}`}
                      className="vega margin-bottom-medium"
                      style={itemStyle}
                    >
                      <div className="vega flex column align-center">
                        <span className="vega text text-center">
                          {item.label && (
                            <strong
                              style={
                                item.status
                                  ? { color: getStatusColor(item.status) }
                                  : undefined
                              }
                            >
                              {item.label}:
                            </strong>
                          )}
                          <span
                            style={
                              item.status
                                ? { color: getStatusColor(item.status) }
                                : undefined
                            }
                          >
                            {item.label ? " " : ""}
                            {item.value}
                          </span>
                        </span>
                      </div>

                      {item.subItems && item.subItems.length > 0 && (
                        <div
                          className="vega margin-top-small"
                          style={{ textAlign: "left", marginTop: "16px" }}
                        >
                          {item.subItems.map((subItem) => (
                            <div
                              key={createSubItemKey(subItem)}
                              className="vega margin-bottom-small vega flex column margin-left-large"
                              style={{ marginBottom: "12px" }}
                            >
                              <div className="vega flex column">
                                <span className="vega text">
                                  {subItem.label && (
                                    <strong
                                      style={
                                        subItem.status
                                          ? {
                                              color: getStatusColor(
                                                subItem.status
                                              ),
                                            }
                                          : undefined
                                      }
                                    >
                                      {subItem.label}:
                                    </strong>
                                  )}
                                  <span
                                    style={
                                      subItem.status
                                        ? {
                                            color: getStatusColor(
                                              subItem.status
                                            ),
                                          }
                                        : undefined
                                    }
                                  >
                                    {subItem.label ? " " : ""}
                                    {subItem.value}
                                  </span>
                                </span>
                              </div>

                              {subItem.records && subItem.records.length > 0 && (
                                <div className="vega margin-left-medium margin-top-small">
                                  {subItem.records.map((record) => (
                                    <div
                                      key={record.id || `record-${record.text}`}
                                      className="vega margin-bottom-xsmall"
                                    >
                                      <span className="vega text text-small">
                                        {record.text}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : (
            // Fallback to simple card details
            <div style={{ width: "100%", maxWidth: "500px" }}>
              {cardDetails.map((detail) => (
                <div
                  key={detail.id || `detail-${detail.label}-${detail.value}`}
                  className="vega margin-bottom-medium"
                  style={itemStyle}
                >
                  <div className="vega flex column align-center">
                    <span className="vega text text-center">
                      {detail.label && (
                        <strong
                          style={
                            detail.status
                              ? { color: getStatusColor(detail.status) }
                              : undefined
                          }
                        >
                          {detail.label}:
                        </strong>
                      )}
                      <span
                        style={
                          detail.status
                            ? { color: getStatusColor(detail.status) }
                            : undefined
                        }
                      >
                        {" "}
                        {detail.value}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ApplicantDetailModal;
